/**
 * backfillPublicationsFromPosts — one-time migration that creates a
 * ContentPublication record for every ContentPost that already has a
 * scheduledAt. Idempotent: keyed by contentPostId so running it twice
 * yields the same set of publications.
 *
 * Runs automatically on first open of the Calendar tab for a strategy
 * that has no publications subcollection yet. A user-initiated "Repair
 * calendar" button also triggers it.
 */

import type { ContentPost } from '../types';
import type { ContentPublication } from '../types';

export interface BackfillInputs {
  projectId: string;
  strategyId: string;
  /** All posts for the strategy across every sprint, pre-flattened. */
  posts: ReadonlyArray<{ sprintId: string; post: ContentPost }>;
  /** Existing publications for the strategy (used for dedup by contentPostId). */
  existingPublications: ReadonlyArray<Pick<ContentPublication, 'id' | 'contentPostId'>>;
}

export interface BackfillResult {
  /** Brand-new publication records to insert. */
  toCreate: ContentPublication[];
  /** Posts skipped because they already have a publication or lack a schedule. */
  skipped: {
    missingSchedule: number;
    alreadyPublished: number;
  };
}

/**
 * Derives the publications that would need to exist for a given set of
 * posts. Pure function — the caller is responsible for the actual
 * Firestore batch write. That separation keeps the migration unit
 * testable without spinning up emulator Firestore.
 */
export function backfillPublicationsFromPosts(input: BackfillInputs): BackfillResult {
  const existingPostIds = new Set(
    input.existingPublications
      .map((p) => p.contentPostId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  );

  const toCreate: ContentPublication[] = [];
  let missingSchedule = 0;
  let alreadyPublished = 0;

  for (const { sprintId, post } of input.posts) {
    const scheduledAt = (post as ContentPost & { scheduledAt?: string | Date }).scheduledAt;
    if (!scheduledAt) {
      missingSchedule++;
      continue;
    }
    if (existingPostIds.has(post.id)) {
      alreadyPublished++;
      continue;
    }

    const iso = typeof scheduledAt === 'string' ? scheduledAt : new Date(scheduledAt).toISOString();
    const weekIndex = (post as ContentPost & { weekIndex?: number }).weekIndex ?? 0;
    const weekdayIndex = (post as ContentPost & { weekdayIndex?: number }).weekdayIndex ?? 0;

    toCreate.push({
      id: `pub_${post.id}`,
      projectId: input.projectId,
      strategyId: input.strategyId,
      sprintId,
      topicId: (post as ContentPost & { topicId?: string }).topicId,
      contentPostId: post.id,
      platform: post.platform,
      scheduledAt: iso,
      weekIndex,
      weekdayIndex,
      source: 'auto-assigned',
    });
  }

  return {
    toCreate,
    skipped: { missingSchedule, alreadyPublished },
  };
}
